import type { IUserRepository } from '../../domain/repositories/UserRepository';
import type { User, CreateUserRequest, UpdateUserRequest } from '../../domain/entities/User';
import type { Result, PaginatedResponse, PaginationParams } from '../../shared/types/common.types';
import type { IApiClient } from '../api/ApiClient';

export class UserRepositoryImpl implements IUserRepository {
  private readonly apiClient: IApiClient;

  constructor(apiClient: IApiClient) {
    this.apiClient = apiClient;
  }

  async findById(id: string): Promise<Result<User>> {
    try {
      const response = await this.apiClient.get<User>(`/users/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }

  async findByEmail(email: string): Promise<Result<User>> {
    try {
      const response = await this.apiClient.get<User>(`/users/email/${email}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }

  async findAll(params: PaginationParams): Promise<Result<PaginatedResponse<User>>> {
    try {
      const response = await this.apiClient.get<PaginatedResponse<User>>(
        `/users?page=${params.page}&limit=${params.limit}`
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }

  async create(userData: CreateUserRequest): Promise<Result<User>> {
    try {
      const response = await this.apiClient.post<User>('/users', userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }

  async update(id: string, userData: UpdateUserRequest): Promise<Result<User>> {
    try {
      const response = await this.apiClient.put<User>(`/users/${id}`, userData);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }

  async delete(id: string): Promise<Result<void>> {
    try {
      await this.apiClient.delete<void>(`/users/${id}`);
      return { success: true, data: undefined };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  }
}
